import { useState, useEffect } from 'react'
import { dataService } from './services/dataService'
import Login from './components/Login'
import Reparto from './components/Reparto'
import Clientes from './components/Clientes'
import Productos from './components/Productos'
import Configuracion from './components/Configuracion'
import Facturacion from './pages/Facturacion'
import Informes from './pages/Informes'
//import InstallPrompt from './components/InstallPrompt'
import { Truck, Users, Wheat, FileText, BarChart3, LogOut, Wifi, WifiOff, RefreshCw, Settings, Menu, X } from 'lucide-react'
import { useOnlineStatus } from './hooks/useOnlineStatus'
import { offlineDB } from './services/offlineDB'
import { syncService } from './services/syncService'
import './App.css'

function App() {
  const [usuario, setUsuario] = useState(null)
  const [vistaActual, setVistaActual] = useState('reparto')
  const { isOnline, justWentOnline, checking } = useOnlineStatus()
  const [isSyncing, setIsSyncing] = useState(false)
  const [syncMessage, setSyncMessage] = useState('')
  const [dbReady, setDbReady] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [menuAbierto, setMenuAbierto] = useState(false)

  // ⭐ NUEVO: Actualizar dataService cuando cambia isOnline
  useEffect(() => {
    dataService.setOnlineStatus(isOnline)
  }, [isOnline])

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768 || 
                     /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
      setIsMobile(mobile)
      if (!mobile) setMenuAbierto(false)
    }
    
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  useEffect(() => {
    inicializarApp()
  }, [])

  async function inicializarApp() {
    try {
      await offlineDB.init()
      console.log('✅ Base de datos offline lista')
      
      if (navigator.onLine) {
        console.log('📥 Precargando datos en caché...')
        await syncService.updateCache()
        console.log('✅ Caché actualizado')
      } else {
        console.log('📵 Sin conexión - usando caché existente')
      }
      
      setDbReady(true)
      
      const userStorage = localStorage.getItem('currentUser')
      if (userStorage) {
        setUsuario(JSON.parse(userStorage))
      }

      syncService.onSyncComplete((result) => {
        setIsSyncing(false)
        if (result.success) {
          setSyncMessage(`✅ ${result.count} elementos sincronizados`)
          setTimeout(() => setSyncMessage(''), 3000)
        } else {
          setSyncMessage('❌ Error en sincronización')
          setTimeout(() => setSyncMessage(''), 3000)
        }
      })
    } catch (error) {
      console.error('❌ Error inicializando app:', error)
      setDbReady(true)
    }
  }

  useEffect(() => {
    if (justWentOnline && dbReady) {
      console.log('🔄 Conexión restaurada, sincronizando...')
      handleSync()
    }
  }, [justWentOnline, dbReady])

  function handleLogin(user) {
    setUsuario(user)
  }

  function handleLogout() {
    localStorage.removeItem('currentUser')
    setUsuario(null)
    setVistaActual('reparto')
    setMenuAbierto(false)
  }

  async function handleSync() {
    if (!navigator.onLine) {
      alert('No hay conexión a internet')
      return
    }

    setIsSyncing(true)
    setSyncMessage('🔄 Sincronizando...')
    await syncService.syncAll()
  }

  function cambiarVista(vista) {
    setVistaActual(vista)
    setMenuAbierto(false)
  }

  const navegacion = [
    { id: 'reparto', nombre: 'Reparto', icono: Truck, roles: ['repartidor', 'admin'] },
    { id: 'clientes', nombre: 'Clientes', icono: Users, roles: ['repartidor', 'admin'] },
    { id: 'productos', nombre: 'Productos', icono: Wheat, roles: ['repartidor', 'admin'] },
    { id: 'facturacion', nombre: 'Facturación', icono: FileText, roles: ['admin'] },
    { id: 'informes', nombre: 'Informes', icono: BarChart3, roles: ['admin'] },
    { id: 'configuracion', nombre: 'Configuración', icono: Settings, roles: ['admin'] }
  ]

  if (!dbReady || checking) { // ⭐ Añadir || checking
    return (
      <div style={{ 
        height: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: 'var(--bg)'
      }}>
        <div style={{ textAlign: 'center', color: 'var(--fg)' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🥖</div>
          <div>Iniciando aplicación...</div>
          <div style={{ fontSize: '0.875rem', color: 'var(--muted)', marginTop: '0.5rem' }}>
            {checking ? 'Verificando conexión...' : 
            navigator.onLine ? 'Descargando datos...' : 'Cargando caché offline...'}
          </div>
        </div>
      </div>
    )
  }

  if (!usuario) {
    return <Login onLogin={handleLogin} />
  }

  const opcionesPermitidas = navegacion.filter(item => 
    item.roles.includes(usuario.rol)
  )

  let rutaInicial = null
  if (usuario.username === 'Ruta 1') rutaInicial = 'Ruta 1'
  if (usuario.username === 'Ruta 2') rutaInicial = 'Ruta 2'

  return (
    <div className="app-container">
      {/* Header Mobile */}
      {isMobile ? (
        <nav className="app-nav-mobile">
          <button 
            className="menu-hamburger"
            onClick={() => setMenuAbierto(!menuAbierto)}
          >
            {menuAbierto ? <X size={24} /> : <Menu size={24} />}
          </button>
          
          <h1 style={{ fontSize: '1rem', margin: 0 }}>🥖 P. Alonso</h1>
          
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <div 
              style={{ 
                display: 'flex',
                alignItems: 'center',
                padding: '0.5rem'
              }}
              title={isOnline ? 'Online' : 'Offline'}
            >
              {isOnline ? <Wifi size={20} style={{ color: 'var(--accent)' }} /> : <WifiOff size={20} style={{ color: 'var(--warn)' }} />}
            </div>
            
            {isOnline && (
              <button 
                onClick={handleSync} 
                disabled={isSyncing}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--fg)',
                  cursor: 'pointer',
                  padding: '0.5rem',
                  display: 'flex',
                  alignItems: 'center'
                }}
                title="Sincronizar"
              >
                <RefreshCw size={20} className={isSyncing ? 'spinning' : ''} />
              </button>
            )}
            
            <button 
              onClick={handleLogout}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--fg)',
                cursor: 'pointer',
                padding: '0.5rem',
                display: 'flex',
                alignItems: 'center'
              }}
              title="Cerrar sesión"
            >
              <LogOut size={20} />
            </button>
          </div>
        </nav>
      ) : (
        // Header Desktop (original)
        <nav className="app-nav">
          <div className="nav-brand">
            <h1>🥖 Panadería Hermanos Alonso</h1>
            <span style={{ fontSize: '0.875rem', color: 'var(--muted)', marginLeft: '1rem' }}>
              {usuario.username}
            </span>
            
          </div>
          <div className="nav-buttons">
            {opcionesPermitidas.map(item => {
              const Icono = item.icono
              return (
                <button
                  key={item.id}
                  onClick={() => setVistaActual(item.id)}
                  className={`nav-btn ${vistaActual === item.id ? 'active' : ''}`}
                  title={item.nombre}
                >
                  <Icono size={20} />
                  <span>{item.nombre}</span>
                </button>
              )
            })}

            <div style={{ 
              marginLeft: 'auto', 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              background: isOnline ? 'rgba(45, 212, 191, 0.1)' : 'rgba(239, 68, 68, 0.1)',
              borderRadius: '6px',
              border: `1px solid ${isOnline ? 'var(--accent)' : 'var(--warn)'}`
            }}>
              {isOnline ? <Wifi size={18} /> : <WifiOff size={18} />}
              <span style={{ fontSize: '0.875rem' }}>
                {isOnline ? 'Online' : 'Offline'}
              </span>
            </div>

            {isOnline && (
              <button 
                onClick={handleSync} 
                disabled={isSyncing}
                className="nav-btn"
                title="Sincronizar datos"
              >
                <RefreshCw size={20} className={isSyncing ? 'spinning' : ''} />
              </button>
            )}

            <button onClick={handleLogout} className="nav-btn" title="Cerrar sesión">
              <LogOut size={20} />
              <span>Cerrar sesión</span>
            </button>
          </div>
        </nav>
      )}

      {/* Menú lateral móvil */}
      {isMobile && menuAbierto && (
        <>
          <div 
            className="menu-overlay"
            onClick={() => setMenuAbierto(false)}
          />
          <div className="menu-lateral">
            <div style={{ padding: '1.5rem', borderBottom: '1px solid var(--line)' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>{usuario.username}</div>
              <div style={{ fontSize: '0.875rem', color: 'var(--muted)' }}>
                {usuario.rol === 'admin' ? 'Administrador' : 'Repartidor'}
              </div>
            </div>
            
            <div style={{ padding: '1rem 0' }}>
              {opcionesPermitidas.map(item => {
                const Icono = item.icono
                return (
                  <button
                    key={item.id}
                    onClick={() => cambiarVista(item.id)}
                    className={`menu-item ${vistaActual === item.id ? 'active' : ''}`}
                  >
                    <Icono size={20} />
                    <span>{item.nombre}</span>
                  </button>
                )
              })}
            </div>
          </div>
        </>
      )}

      {syncMessage && (
        <div style={{
          position: 'fixed',
          top: isMobile ? '70px' : '80px',
          right: '20px',
          background: 'var(--bg-secondary)',
          border: '1px solid var(--accent)',
          borderRadius: '8px',
          padding: '1rem 1.5rem',
          zIndex: 1000,
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
        }}>
          {syncMessage}
        </div>
      )}

      <main className="app-main">
        {vistaActual === 'reparto' && <Reparto usuario={usuario} rutaInicial={rutaInicial} isOnline={isOnline} />}
        {vistaActual === 'clientes' && <Clientes usuario={usuario} />}
        {vistaActual === 'productos' && <Productos usuario={usuario} />}
        {vistaActual === 'configuracion' && <Configuracion usuario={usuario} />}
        {vistaActual === 'facturacion' && <Facturacion />}
        {vistaActual === 'informes' && <Informes />}
      </main>
    </div>
  )
}

export default App